import { getMetricIdFromVarName, getColorRange } from './metrics'
import {
  isGapVarName,
  getDemographicIdFromVarName,
  isGapDemographic
} from './demographics'
import {
  getValuePositionInRange,
  formatPercentDiff,
  formatPercent,
  formatNumber,
  getPositionFromValue
} from '../../../../shared/utils'
import { getStateAbbr } from '../../../../shared/utils/states'
import { getRegionFromLocationId } from './regions'
import { NO_SES_DEMOGRAPHICS } from '../constants/demographics'

// Values provided by SEDA team for calculation distance from regression
// shared in the "function of ses-achievement relationship clean.xlsx" file
// included in their data package for the ETL. These numbers need to be copied
// the file and added to the object below
const FUNC_VARS = {
  states: {
    avg: null,
    grd: null,
    coh: null,
    imp: null
  },
  // counties: {
  //   avg: [-0.117, 0.768, 0.053, 0.033, 3133],
  //   grd: [0.981, 0.042, 0.000, 0.000, 3066],
  //   coh: [-0.002, -0.003, 0.004, 0.003, 3101],
  //   imp: [-0.002, -0.003, 0.004, 0.003, 3101]
  // },
  districts: {
    avg: [-0.957, 0.935, 0.146, 0.065, 10177],
    grd: [0.913, 0.022, 0.028, 0.001, 8136],
    coh: [-0.032, -0.016, 0.021, 0.002, 7595],
    imp: [-0.019, 0.019, -0.001, -0.000, 5975]
  },
  schools: {
    avg: [2.971, -8.952, 6.962, -3.820, 50769],
    grd: [1.183, -0.602, 0.435, 0.011, 39002],
    coh: [0.031, -0.127, -0.001, 0.166, 42128],
    imp: [-0.001, 0.023, -0.111, 0.052, 21518]
  }
}

/**
 * Gets how far the provided value is from the regression
 * @param {*} value
 * @param {*} metricId
 * @param {*} regionId
 */
export const getPredictedValue = (value, metricId, regionId) => {
  // only have predicted values for counties, districts and schools
  if (
    // ['counties', 'districts', 'schools'].indexOf(regionId) === -1
    ['districts', 'schools'].indexOf(regionId) === -1
  )
    return null
  const b = FUNC_VARS[regionId][metricId]
  const result =
    b[0] +
    b[1] * value +
    b[2] * Math.pow(value, 2) +
    b[3] * Math.pow(value, 3)
  return result
}

/** checks if value is present */
export const hasVal = val => val || val === 0

/**
 * Returns an array containing the min and max for the
 * provided varname and region
 */
export const getColorRangeForVarName = (
  varName,
  region,
  type
) => {
  const metricId = getMetricIdFromVarName(varName)
  const demId = getDemographicIdFromVarName(varName)
  return getColorRange(metricId, demId, region, type)
}

/**
 * Returns the diverging midpoint for the provided varName
 * @param {string} varName
 */
export const getMidpointForVarName = varName => {
  if (
    varName.split('_')[1] === 'grd' &&
    !isGapVarName(varName)
  ) {
    return 1
  }
  // set midpoint out of view for FRL
  if (varName.split('_')[1] === 'frl') {
    return -10
  }
  return 0
}

/**
 * Gets the position of where the bar should extend to
 * for a value based on the metric
 * @param {string} varName
 * @param {number} value
 * @param {array} range
 */
export const getPositionForVarNameValue = (
  varName,
  value,
  range
) => {
  const midPoint = getMidpointForVarName(varName)
  return varName.indexOf('frl') > -1
    ? getValuePositionInRange(value, range)
    : getPositionFromValue(value, range, midPoint)
}

/**
 * Returns a function to format values for the provided varName
 * @param {*} varName
 */
export const getFormatterForVarName = varName => {
  const metric = getMetricIdFromVarName(varName)
  const isGap = isGapVarName(varName)

  if (isGap && metric === 'grd') {
    return formatPercent
  }
  switch (metric) {
    case 'grd':
      return formatPercentDiff
    case 'frl':
    case 'seg':
    case 'min':
      return formatPercent
    default:
      return formatNumber
  }
}

const getMetricVarName = (metric, demographic = 'all') => {
  return demographic + '_' + metric
}

/**
 * Gets the secondary var name for the given demographic
 * and substitues "all" for the demographic if SES is
 * unavailable for the given demographic.
 * @param {*} secondary
 * @param {*} demographic
 * @returns
 */
const getSecondaryVarName = (secondary, demographic) => {
  // use "all" SES option for the following demographics
  if (secondary === 'ses') {
    const useAll = NO_SES_DEMOGRAPHICS.indexOf(demographic) > -1
    return useAll ? 'all_ses' : demographic + '_ses'
  }
  return demographic + '_' + secondary
}

const getSizeVarName = demographic => {
  return demographic + '_sz'
}

/**
 * Returns x,y,z variable names for the versus scatterplot
 * @param {string} metric  (metric id)
 * @param {string} demographic (gap demographic id)
 */
const getVersusVarNames = (metric, demographic) => {
  let dem1 = demographic[0]
  let dem2 = demographic[1]
  // if poor / non-poor, get correct demographic and order
  if (dem2 === 'n') {
    dem1 = 'np'
    dem2 = 'p'
  }
  return [
    dem1 + '_' + metric,
    dem2 + '_' + metric,
    demographic + '_sz'
  ]
}

/**
 * Returns the x,y,z variables for the current selection
 * @param {string} region id
 * @param {string} metric id
 * @param {string} demographic id
 * @param {string} type ("chart" or "map")
 */
export const getVarNames = (
  region,
  metric,
  secondary = 'ses',
  demographic,
  type = 'chart'
) => {
  const isGap = isGapDemographic(demographic)
  // schools always use "all" subgroup
  if (region === 'schools') {
    return ['all_frl', 'all_' + metric, 'all_sz']
  }
  // default chart for gap demographics should be versus
  if (type === 'chart' && isGap) {
    return getVersusVarNames(metric, demographic)
  }
  // always use SES as secondary when no gap
  const secondaryVarName = getSecondaryVarName(
    isGap ? secondary : 'ses',
    demographic
  )
  return [
    secondaryVarName,
    getMetricVarName(metric, demographic),
    getSizeVarName(demographic)
  ]
}

/**
 * Mergest data from the feature store and the
 * scatterplot store for the provided identifier.
 * @param {*} id
 * @param {*} data
 */
export const getDataForId = (id, data, featureData = {}) => {
  if (!data && !featureData) return null
  data = data || {}
  featureData = featureData || {}
  const feature = featureData[id] || {}
  const base = {
    id,
    state: getStateAbbr(id),
    region: getRegionFromLocationId(id),
    ...feature
  }
  return Object.keys(data).reduce((acc, curr) => {
    // only add data if it exists
    if (data[curr][id] || data[curr][id] === 0) {
      acc[curr] = data[curr][id]
    }
    return acc
  }, base)
}

export const isUnavailable = value => {
  return value === -999 || (!value && value !== 0)
}
